import { useEffect, useRef } from "react";
import type { GenRow } from "../api/types";

// Faithful port of the Evo Console's drawFitness(): best_so_far (green, bold),
// gen_best (blue), mean (orange, clipped + red when off-scale), and the dashed
// est-max ceiling.
function draw(canvas: HTMLCanvasElement, rows: GenRow[], estMax0: number | null) {
  const wrap = canvas.parentElement!;
  const W = wrap.clientWidth;
  const H = wrap.clientHeight;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);
  if (rows.length === 0) return;

  const PAD_L = 50, PAD_R = 10, PAD_T = 10, PAD_B = 22;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const xs = rows.map((r) => r.gen);
  let estMax: number | null = estMax0;
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const xRange = xMax - xMin || 1;
  const finite = (a: number[]) => a.filter(Number.isFinite);
  const bestVals = finite(rows.map((r) => r.best_so_far));
  if (estMax !== null && bestVals.length && estMax < Math.max(...bestVals)) estMax = null;
  const signalYs = finite([...rows.map((r) => r.gen_best), ...rows.map((r) => r.best_so_far)]);
  if (estMax !== null) signalYs.push(estMax);
  const bestLo = Math.min(...signalYs), bestHi = Math.max(...signalYs);
  const bestSpan = bestHi - bestLo || 1;
  const meanFloor = bestLo - 1.5 * bestSpan;
  const inlierMeans = finite(rows.map((r) => r.mean)).filter((v) => v >= meanFloor);
  const yLo = Math.min(bestLo, ...(inlierMeans.length ? inlierMeans : [bestLo]));
  const yHi = bestHi;
  const yPad = (yHi - yLo) * 0.08 || 1;
  const yMin = yLo - yPad, yMax = yHi + yPad;
  const yRange = yMax - yMin || 1;
  const sx = (x: number) => PAD_L + ((x - xMin) / xRange) * plotW;
  const sy = (y: number) => PAD_T + (1 - (y - yMin) / yRange) * plotH;

  ctx.strokeStyle = "#2a2a2a";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD_L, PAD_T);
  ctx.lineTo(PAD_L, PAD_T + plotH);
  ctx.lineTo(PAD_L + plotW, PAD_T + plotH);
  ctx.stroke();

  ctx.fillStyle = "#666";
  ctx.font = '9px ui-monospace, monospace';
  ctx.textAlign = "right";
  for (let i = 0; i <= 4; i++) {
    const yv = yMin + (yRange * i) / 4;
    const yp = sy(yv);
    ctx.strokeStyle = "#1e1e1e";
    ctx.beginPath();
    ctx.moveTo(PAD_L, yp);
    ctx.lineTo(PAD_L + plotW, yp);
    ctx.stroke();
    ctx.fillText(yv.toFixed(3), PAD_L - 4, yp + 3);
  }
  ctx.textAlign = "center";
  for (let i = 0; i <= 5; i++) {
    const xv = Math.round(xMin + (xRange * i) / 5);
    ctx.fillText(`${xv}`, sx(xv), PAD_T + plotH + 12);
  }

  // mean line, clipped to range; red where off-scale
  let pX: number | null = null, pY = 0, pOff = false;
  for (let i = 0; i < rows.length; i++) {
    const v = rows[i].mean, x = sx(rows[i].gen);
    const off = !(Number.isFinite(v) && v >= yMin);
    const y = off ? sy(yMin) : sy(v > yMax ? yMax : v);
    if (pX !== null) {
      ctx.strokeStyle = off || pOff ? "#e84040" : "#e8a020";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pX, pY);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    pX = x; pY = y; pOff = off;
  }
  const plotLine = (key: "gen_best" | "best_so_far", color: string, w: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = w;
    ctx.beginPath();
    for (let i = 0; i < rows.length; i++) {
      const x = sx(rows[i].gen), y = sy(rows[i][key]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };
  plotLine("gen_best", "#4a9eff", 1.2);
  plotLine("best_so_far", "#3dba6e", 2);

  if (estMax !== null) {
    const yp = sy(estMax);
    ctx.strokeStyle = "#c8c8c8";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(PAD_L, yp);
    ctx.lineTo(PAD_L + plotW, yp);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#c8c8c8";
    ctx.textAlign = "right";
    ctx.fillText(`max ${estMax.toFixed(3)}`, PAD_L + plotW - 4, yp - 3);
  }
}

export function FitnessCanvas({ rows, estMax }: { rows: GenRow[]; estMax: number | null }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const render = () => draw(c, rows, estMax);
    render();
    const ro = new ResizeObserver(render);
    ro.observe(c.parentElement!);
    return () => ro.disconnect();
  }, [rows, estMax]);
  return (
    <div className="viz-wrap">
      {rows.length === 0 && <div className="viz-empty">waiting for generations…</div>}
      <canvas ref={ref} />
    </div>
  );
}
